import { Vector2 } from "../../../../common/vector2.js";
import { AudioClips } from "../../../../audio/audio_constants.js";
import { audioController } from "../../../../audio/audio_controller.js";
import { hapticsController } from "../../../../haptics/haptics_controller.js";
import { ImgID } from "../../../../image_loader/img_id.js";
import { ScreenManager, Screens } from "../../../../screen_manager/screen_manager.js";
import { Easing } from "../../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../../transforms/transform_opacity.js";
import { TransformScale } from "../../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../../transforms/transform_straight_move.js";
import { RoundedRectangle } from "../../../common_ui_elements/rounded_rectangle.js";
import { UIElement } from "../../../common_ui_elements/ui_element.js";
import { UIImage } from "../../../common_ui_elements/ui_image.js";
import { PlayDialog, PlayDialogState } from "../../../dialog/play_dialog.js";
import { DialogManager } from "../../../dialog/dialog_manager.js";
import { getLevelDifficulty } from "../../../../level_config.js";
import { Game } from "../../../../game.js";

interface MenuButton
{
  el: UIImage;
  collapsed_y: number;
  expanded_y: number;
}

// Quick in-game settings that unfold from beneath the settings gear: sound,
// music and vibration toggles (each swaps its on/off art) plus an exit button.
// They cascade downwards with a staggered slide + pop, over a dim scrim
// that also closes the menu when tapped outside a button.
export class GameSettingsMenu extends UIElement
{
  private static readonly GAP_DESIGN = 33;
  private static readonly ANIM_MS = 192;
  private static readonly STAGGER_MS = 44;
  private static readonly CLOSE_ANIM_MS = 16;
  private static readonly CLOSE_STAGGER_MS = 4;
  private static readonly COLLAPSED_SCALE = 0.4;

  private readonly btn_x: number;
  private readonly btn_w: number;
  private readonly btn_h: number;
  private readonly anchor_y: number;

  private scrim!: RoundedRectangle;
  private sound_btn!: UIImage;
  private music_btn!: UIImage;
  private vibration_btn!: UIImage;
  private exit_btn!: UIImage;
  private buttons: MenuButton[] = [];

  private is_open = false;
  private menu_animating = false;
  private anim_end_at = 0;
  private closing = false;

  private request_clean_render: () => void;
  private on_exit: () => void;

  constructor(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    btn_x: number,
    btn_y: number,
    btn_w: number,
    btn_h: number,
    request_clean_render: () => void,
    on_exit: () => void
  )
  {
    super( ctx, 0, 0, width, height, false, false );

    console.log('GameSettingsMenu created', this.rect.getW(), this.rect.getH());

    this.btn_x = btn_x;
    this.btn_w = btn_w;
    this.btn_h = btn_h;
    this.anchor_y = btn_y;
    this.request_clean_render = request_clean_render;
    this.on_exit = on_exit;

    this.createScrim();
    this.createButtons();
    this.applyCollapsedState();
  }

  private createScrim(): void
  {
    this.scrim = new RoundedRectangle(
      this.ctx, -Game.safeAreaLeft(), -Game.safeAreaTop(), this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight(), this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom(), 0, "rgba(0, 0, 0, 0.35)"
    );
    this.scrim.setOnClick( () => { if ( !this.menu_animating ) this.close(); } );
    this.addChild( this.scrim );
  }

  private createButtons(): void
  {
    const gap  = LayoutScaler.heightFromDesign( GameSettingsMenu.GAP_DESIGN );
    const step = this.btn_h + gap;
    const first_y = this.anchor_y + this.btn_h + gap;

    this.sound_btn = this.createButton( this.soundImg() );
    this.music_btn = this.createButton( this.musicImg() );
    this.vibration_btn = this.createButton( this.vibrationImg() );
    this.exit_btn  = this.createButton( ImgID.UI_GameExit );

    this.sound_btn.setOnClick( () => { void this.onToggleSound(); } );
    this.music_btn.setOnClick( () => { void this.onToggleMusic(); } );
    this.vibration_btn.setOnClick( () => { void this.onToggleVibration(); } );
    this.exit_btn.setOnClick( () => this.onExit() );

    [ this.sound_btn, this.music_btn, this.vibration_btn, this.exit_btn ].forEach( ( el, i ) =>
    {
      this.buttons.push( { el, collapsed_y: this.anchor_y, expanded_y: first_y + i * step } );
    } );
  }

  private createButton( img: ImgID ): UIImage
  {
    const btn = new UIImage( this.ctx, this.btn_x, this.anchor_y, this.btn_w, this.btn_h, img, {}, true );
    this.addChild( btn );
    return btn;
  }

  private soundImg(): ImgID
  {
    return audioController.is_sounds_enabled ? ImgID.UI_GameSoundOn : ImgID.UI_GameSoundOff;
  }

  private musicImg(): ImgID
  {
    return audioController.is_music_enabled ? ImgID.UI_GameMusicOn : ImgID.UI_GameMusicOff;
  }

  private vibrationImg(): ImgID
  {
    return hapticsController.isHapticsEnabled()
      ? ImgID.UI_GameVibrationOn
      : ImgID.UI_GameVibrationOff;
  }

  private applyCollapsedState(): void
  {
    this.scrim.setClickable( false );
    this.scrim.setOpacity( 0 );

    for ( const b of this.buttons )
    {
      b.el.setPixelPos( new Vector2( this.btn_x, b.collapsed_y ), true );
      b.el.setScale( new Vector2( GameSettingsMenu.COLLAPSED_SCALE, GameSettingsMenu.COLLAPSED_SCALE ) );
      b.el.setOpacity( 0 );
      b.el.setClickable( false );
    }
  }

  public isOpen(): boolean { return this.is_open; }
  public isBusy(): boolean { return this.is_open || this.menu_animating; }

  public toggle(): void
  {
    if ( this.menu_animating ) return;
    if ( this.is_open ) this.close();
    else this.open();
  }

  public open(): void
  {
    if ( this.is_open || this.menu_animating ) return;

    this.is_open = true;
    this.menu_animating = true;
    this.closing = false;

    const now = performance.now();

    this.scrim.setClickable( true );
    this.scrim.addTransform( new TransformOpacity( this.scrim, true, null, 1, now, GameSettingsMenu.ANIM_MS, { easing: Easing.EaseOutQuad } ) );

    this.buttons.forEach( ( b, i ) =>
    {
      const start = now + i * GameSettingsMenu.STAGGER_MS;

      b.el.setClickable( true );
      b.el.setPixelPos( new Vector2( this.btn_x, b.collapsed_y ), true );
      b.el.setScale( new Vector2( GameSettingsMenu.COLLAPSED_SCALE, GameSettingsMenu.COLLAPSED_SCALE ) );
      b.el.setOpacity( 0 );

      b.el.addTransform( new TransformStraightMove( b.el, true, null, new Vector2( this.btn_x, b.expanded_y ), start, GameSettingsMenu.ANIM_MS, { easing: Easing.EaseOutQuad } ) );
      b.el.addTransform( new TransformScale( b.el, true, null, new Vector2( 1, 1 ), start, GameSettingsMenu.ANIM_MS, { easing: Easing.EaseOutQuad } ) );
      b.el.addTransform( new TransformOpacity( b.el, true, 0, 1, start, GameSettingsMenu.ANIM_MS, { easing: Easing.EaseOutQuad } ) );
    } );

    this.scheduleAnimEnd( ( this.buttons.length - 1 ) * GameSettingsMenu.STAGGER_MS + GameSettingsMenu.ANIM_MS );
  }

  public close(): void
  {
    if ( !this.is_open || this.menu_animating ) return;

    this.menu_animating = true;
    this.closing = true;

    const now = performance.now();
    const n = this.buttons.length;

    this.scrim.setClickable( false );
    this.scrim.addTransform( new TransformOpacity( this.scrim, true, null, 0, now, GameSettingsMenu.CLOSE_ANIM_MS, { easing: Easing.EaseInQuad } ) );

    this.buttons.forEach( ( b, i ) =>
    {
      // Bottom button (exit) tucks away first for a natural roll-up.
      const start = now + ( n - 1 - i ) * GameSettingsMenu.CLOSE_STAGGER_MS;

      b.el.setClickable( false );

      b.el.addTransform( new TransformStraightMove( b.el, true, null, new Vector2( this.btn_x, b.collapsed_y ), start, GameSettingsMenu.CLOSE_ANIM_MS, { easing: Easing.EaseInQuad } ) );
      b.el.addTransform( new TransformScale( b.el, true, null, new Vector2( GameSettingsMenu.COLLAPSED_SCALE, GameSettingsMenu.COLLAPSED_SCALE ), start, GameSettingsMenu.CLOSE_ANIM_MS, { easing: Easing.EaseInQuad } ) );
      b.el.addTransform( new TransformOpacity( b.el, true, null, 0, start, GameSettingsMenu.CLOSE_ANIM_MS, { easing: Easing.EaseInQuad } ) );
    } );

    this.scheduleAnimEnd( ( n - 1 ) * GameSettingsMenu.CLOSE_STAGGER_MS + GameSettingsMenu.CLOSE_ANIM_MS );
  }

  private scheduleAnimEnd( duration_ms: number ): void
  {
    this.anim_end_at = performance.now() + duration_ms + 20;
  }

  public tick( now: number ): void
  {
    if ( !this.menu_animating ) return;
    if ( now < this.anim_end_at ) return;

    this.menu_animating = false;

    if ( this.closing )
    {
      this.closing = false;
      this.is_open = false;
      this.applyCollapsedState();
      // Everything is invisible now, but the last animated frame left faint
      // trails on the shared overlay canvas — repaint it once, clean.
      this.request_clean_render();
    }
  }

  private async onToggleSound(): Promise<void>
  {
    const next = !audioController.is_sounds_enabled;

    // Play before disabling, but enable sounds first when they are currently off.
    if ( next )
    {
      await audioController.enableSounds( true );
      void audioController.playSound( AudioClips.UI_CLICK );
    }
    else
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      await audioController.enableSounds( false );
    }

    this.sound_btn.setImageId( this.soundImg() );
  }

  private async onToggleMusic(): Promise<void>
  {
    void audioController.playSound( AudioClips.UI_CLICK );
    const next = !audioController.is_music_enabled;
    await audioController.enableMusic( next );
    this.music_btn.setImageId( this.musicImg() );
  }

  private async onToggleVibration(): Promise<void>
  {
    void audioController.playSound( AudioClips.UI_CLICK );

    const next = !hapticsController.isHapticsEnabled();
    hapticsController.enableHaptics( next );
    if ( next )
      await hapticsController.vibratePreview();

    this.vibration_btn.setImageId( this.vibrationImg() );
  }

  private onExit(): void
  {
    void audioController.playSound( AudioClips.UI_CLICK );

    this.on_exit();
  }

  public name(): string { return "GameSettingsMenu"; }

  // While closed the full-screen container must be transparent to input, or it
  // would swallow the very tap on the gear that's meant to open it.
  public onTouchStart( p: Vector2, now: number ): boolean
  {
    if ( !this.is_open && !this.menu_animating )
      return false;
    return super.onTouchStart( p, now );
  }

  public render(): void
  {
    if ( !this.is_open && !this.menu_animating )
    {
      this.skipRenderSubtree();
      return;
    }
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
